import { CLEIC_APP_CHANNEL_QUERY_KEY } from './app-channel.js';

const LEDGER_URLS_BY_CHANNEL = Object.freeze({
  live: 'https://ledger.cleic.com',
  preview: 'https://preview-ledger.cleic.com',
  lab: 'https://lab-ledger.cleic.com',
});

const STANDALONE_ESCAPE_QUERY_KEY = 'cleic_lens_standalone';
const LEDGER_HANDOFF_QUERY_KEY = 'cleic_lens_handoff';

function normalizeChannel(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'live' || normalized === 'prod' || normalized === 'production') return 'live';
  if (normalized === 'preview' || normalized === 'pr') return 'preview';
  if (normalized === 'lab' || normalized === 'staging' || normalized === 'stage') return 'lab';
  return null;
}

function inferChannelFromHost(hostname) {
  const host = String(hostname ?? '').trim().toLowerCase();
  if (host === 'lab-lens.cleic.com' || host === 'lab-ledger.cleic.com') return 'lab';
  if (host === 'preview-lens.cleic.com' || host === 'preview-ledger.cleic.com') return 'preview';
  if (host === 'lens.cleic.com' || host === 'ledger.cleic.com') return 'live';
  return null;
}

function isProductionLensHost(hostname) {
  const host = String(hostname ?? '').trim().toLowerCase();
  return host === 'lens.cleic.com' || host === 'preview-lens.cleic.com' || host === 'lab-lens.cleic.com';
}

function mapLensPathToLedgerPath(pathname) {
  const path = String(pathname || '/').toLowerCase();
  if (path.startsWith('/actuals/je-list') || path.startsWith('/actuals/je-new')) return '/journal-entries';
  if (path.startsWith('/actuals')) return '/actuals/reports';
  if (path.startsWith('/budgets')) return '/budgets/workspace';
  if (path.startsWith('/forecasts')) return '/forecasts/workspace';
  if (path.startsWith('/support-stack/write-intents')) return '/actuals/relationships';
  if (path.startsWith('/support-stack')) return '/sub-ledgers';
  if (path.startsWith('/cash-monitor')) return '/cash-monitor';
  if (path.startsWith('/synthesis/trajectory')) return '/synthesis/trajectory';
  if (path.startsWith('/synthesis/variance')) return '/synthesis/variance';
  if (path.startsWith('/synthesis/tornado')) return '/synthesis/tornado';
  if (path.startsWith('/synthesis/contribution')) return '/synthesis/contribution';
  if (path.startsWith('/synthesis/ledger-projection')) return '/synthesis/ledger-projection';
  if (path.startsWith('/synthesis/formal')) return '/synthesis/formal';
  if (path.startsWith('/synthesis/comparative')) return '/synthesis/comparative';
  if (path.startsWith('/synthesis')) return '/synthesis';
  if (path.startsWith('/fcc/three-stmt')) return '/fcc/three-stmt';
  if (path.startsWith('/fcc/liquid')) return '/fcc/liquid';
  if (path.startsWith('/fcc/nc-assets-liabilities')) return '/fcc/nc-assets-liabilities';
  if (path.startsWith('/fcc/journal-entries')) return '/fcc/journal-entries';
  if (path.startsWith('/fcc/transactions')) return '/fcc/transactions';
  if (path.startsWith('/fcc/comparative')) return '/fcc/comparative';
  if (path.startsWith('/fcc')) return '/fcc';
  if (path.startsWith('/drivers/recurring')) return '/drivers/recurring';
  if (path.startsWith('/drivers/model-drivers')) return '/drivers/model-drivers';
  if (path.startsWith('/drivers/piecewise')) return '/drivers/piecewise';
  if (path.startsWith('/drivers')) return '/drivers';
  if (path.startsWith('/settings/profiles')) return '/settings/profiles';
  if (path.startsWith('/settings/model-assumptions')) return '/settings/model-assumptions';
  if (path.startsWith('/settings/visibility')) return '/settings/visibility';
  if (path.startsWith('/settings/guides')) return '/settings/guides';
  if (path.startsWith('/settings/presentation')) return '/settings/presentation';
  if (path.startsWith('/settings/snapshot')) return '/settings/snapshot';
  if (path.startsWith('/settings')) return '/settings';
  return '/actuals/reports';
}

export function resolveLensMigrationTarget(currentUrl) {
  const source = new URL(currentUrl);
  const explicitChannel = normalizeChannel(source.searchParams.get(CLEIC_APP_CHANNEL_QUERY_KEY));
  const channel = explicitChannel ?? inferChannelFromHost(source.hostname) ?? 'live';
  const target = new URL(mapLensPathToLedgerPath(source.pathname), LEDGER_URLS_BY_CHANNEL[channel]);

  for (const [key, value] of source.searchParams.entries()) {
    if (key === STANDALONE_ESCAPE_QUERY_KEY) continue;
    target.searchParams.set(key, value);
  }
  if (channel !== 'live' || explicitChannel) {
    target.searchParams.set(CLEIC_APP_CHANNEL_QUERY_KEY, channel);
  }
  target.hash = source.hash;
  return target.toString();
}

export function shouldRedirectLensToLedger(location = window.location) {
  const url = new URL(location.href);
  if (url.searchParams.get(STANDALONE_ESCAPE_QUERY_KEY) === '1') return false;
  if (!isProductionLensHost(url.hostname)) return false;
  return url.searchParams.get(LEDGER_HANDOFF_QUERY_KEY) === 'ledger';
}

export function redirectLensToLedgerIfNeeded(location = window.location) {
  if (!shouldRedirectLensToLedger(location)) return false;
  window.location.replace(resolveLensMigrationTarget(location.href));
  return true;
}
